import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
  styled,
} from '@mui/material';
import StepConnector, { stepConnectorClasses } from '@mui/material/StepConnector';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  usePreRegRegistrationDetailsQuery,
  useUpdateRoomAssignmentMutation,
  useAdvancePaymentUpdateMutation,
  useCheckInPreRegMutation,
  useIsXenditQuery,
  usePreRegistrationPaymentAttemptStatusQuery,
  useUpdateIsPreCheckinMutation
} from '../generated/graphql';
import { buildPreCheckInUrl } from '../Components/helper/preCheckInUrl';
import { MainWrapper } from '../Components/MainWrapper';
import { ChooseRoom } from './FormComponent/ChooseRoom';
import { Payment } from './FormComponent/Payment';
import { AssignRoom } from './FormComponent/AssignRoom';
import { ConfirmCheckIn } from './FormComponent/ConfirmCheckIn';
import Loading from '../Components/Loading/Loading';
import { Selfie } from './FormComponent/Selfie';
import { ConfirmPayment } from './FormComponent/ConfirmPayment';
import { PreAuthorisation } from './FormComponent/PreAuthorisation';
import { useSnackBar } from '../Components/Hooks/useSnackBar';
import SnackBarMsg from '../Components/SnackBar/SnackBarMsg';
import { XenditPaymentStatus } from './FormComponent/XenditPaymentStatus';

const StepIconConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 10,
    left: 'calc(-50% + 16px)',
    right: 'calc(50% + 16px)',
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: '#ff9800',
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: '#ff9800',
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : '#d3d3d3',
    borderRadius: 1,
  },
}));

const StepIcon = (props) => {
  const { active, completed } = props;
  return completed ? (
    <CheckCircleIcon style={{ fontSize: '20px', color: '#ff9800' }} />
  ) : active ? (
    <CheckCircleOutlineIcon style={{ fontSize: '20px', color: '#ff9800' }} />
  ) : (
    <CheckCircleOutlineIcon style={{ fontSize: '20px', color: '#d3d3d3' }} />
  );
};

type ContentStep = 'selfie' | 'chooseRoom' | 'payment' | 'preAuth' | 'confirm';

const STEP_LABELS: Record<Exclude<ContentStep, 'preAuth'>, string> = {
  selfie: 'Selfie',
  chooseRoom: 'Choose Room',
  payment: 'Payment',
  confirm: 'Confirm',
};

// Pre-auth (trip 2, the card hold) is a sub-step of Payment — it never gets its own
// stepper bubble. The guest sees "Payment" stay highlighted while the hold is created.
const STEPPER_LABEL_STEP: Record<ContentStep, Exclude<ContentStep, 'preAuth'>> = {
  selfie: 'selfie',
  chooseRoom: 'chooseRoom',
  payment: 'payment',
  preAuth: 'payment',
  confirm: 'confirm',
};

const buildContentSteps = (
  dateControl: boolean,
  enableSelfie: boolean,
  onlinePayment: boolean,
  includePreAuth: boolean
): ContentStep[] => {
  const steps: ContentStep[] = [];
  if (dateControl && enableSelfie) {
    steps.push('selfie');
  }
  if (dateControl && onlinePayment) {
    steps.push('chooseRoom', 'payment');
    if (includePreAuth) {
      steps.push('preAuth');
    }
    steps.push('confirm');
  } else {
    steps.push('confirm');
  }
  return steps;
};

const contentStepNumber = (steps: ContentStep[], key: ContentStep) => {
  const index = steps.indexOf(key);
  return index >= 0 ? index + 1 : 1;
};

export const PreCheckInFormV2 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const localstate: any = location.state;

  const routeParams = useParams();
  const queryParameters = new URLSearchParams(window.location.search);
  const status = (queryParameters.get('status') || routeParams.status) as string;
  const orderID = (queryParameters.get('orderId') || routeParams.orderId) as string;
  const normalizedStatus = status?.toUpperCase();
  const cancelledUrl = normalizedStatus === 'CANCEL' || normalizedStatus === 'CANCELLED';
  const IsOnlinePayment = JSON.parse(localStorage.getItem('IsOnlinePayment') as any);
  const EnableSelfieStorage = JSON.parse(localStorage.getItem('EnableSelfie') as any);
  const SelfCheckinStorage = JSON.parse(localStorage.getItem('SelfCheckin') as any);
  const SelfChooseRoomStorage = JSON.parse(localStorage.getItem('SelfChooseRoom') as any);
  const PreCheckinAdvTimeStorage = Number(localStorage.getItem('PreCheckinAdvTime') || 0);
  let registrationDetailsStorage: any = null;
  try {
    registrationDetailsStorage = JSON.parse(localStorage.getItem('RegistrationDetails') as any);
  } catch {
    registrationDetailsStorage = null;
  }

  const { snackFunc, snackBarMessage, openSnackBar, close } = useSnackBar()
  
  const [activeStep, setActiveStep] = useState(1);
  const [assignRoom, setAssignRoom] = useState(false);
  const [roomSelect, setRoomSelect] = useState(null);
  const [paymentPollingTimedOut, setPaymentPollingTimedOut] = useState(false);
  const [isRedirectingAfterSuccess, setIsRedirectingAfterSuccess] = useState(false);
  const returnHandledRef = useRef(false);
  const returnStepPlacedRef = useRef(false);
  const completionHandledRef = useRef(false);
  const paymentPollingTimeoutRef = useRef<number | null>(null);
  const successHoldTimeoutRef = useRef<number | null>(null);

  // brief pause on completed / authorized status before advancing
  const SUCCESS_STATUS_HOLD_MS = 1500;

  const isSelfCheckin = localstate?.SelfCheckin ?? SelfCheckinStorage;
  const isSelfChooseRoom = localstate?.SelfChooseRoom ?? SelfChooseRoomStorage;
  const PreCheckinAdvTime = localstate?.PreCheckinAdvTime ?? PreCheckinAdvTimeStorage;
  const ArrivalDate =
    localstate?.ArrivalDate ?? registrationDetailsStorage?.ArrivalDate;
  const isOnlinePayment = localstate?.IsOnlinePayment === true || IsOnlinePayment === true;
  const isEnableSelfie = localstate?.EnableSelfie === true || EnableSelfieStorage === true;
  const LNAProcessTime = JSON.parse(localStorage.getItem('HotelDetails') as any)?.NAProcessTime

  const currentDate = new Date();
  const preCheckinAdvTime = PreCheckinAdvTime || 0
  const arrivalDate = ArrivalDate ? new Date(ArrivalDate) : null
  const windowStartDate = arrivalDate
    ? new Date(arrivalDate.getTime() - preCheckinAdvTime * 60 * 60 * 1000)
    : null
  const naHour = LNAProcessTime ? new Date(LNAProcessTime).getHours() : 0
  const naCutoffDate = arrivalDate
    ? new Date(arrivalDate.getFullYear(), arrivalDate.getMonth(), arrivalDate.getDate() + 1, naHour, 0, 0, 0)
    : null
  const dateControl =
    !!arrivalDate &&
    !!windowStartDate &&
    !!naCutoffDate &&
    currentDate >= windowStartDate &&
    currentDate <= naCutoffDate

  const paymentAttemptID = localStorage.getItem('PaymentAttemptID') || '';
  const paymentRequestRef = localStorage.getItem('PaymentRequestRef') || '';
  const preAuthAttemptID = localStorage.getItem('PreAuthAttemptID') || '';
  const preAuthRequestRef = localStorage.getItem('PreAuthRequestRef') || '';
  const preAuthElected = localStorage.getItem('PreAuthElected') === 'true';
  const paymentHotelID = localstate?.HotelID ||
    JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.Booking?.HotelID || '';
  const isPreAuthReturn = Boolean(orderID && preAuthRequestRef && orderID === preAuthRequestRef);
  const isPaymentReturn = Boolean(orderID && paymentRequestRef && orderID === paymentRequestRef);
  const pollAttemptID = isPreAuthReturn ? preAuthAttemptID : paymentAttemptID;
  const pollRequestRef = isPreAuthReturn ? preAuthRequestRef : paymentRequestRef;
  const hasValidPaymentContinuation = Boolean(
    pollAttemptID &&
      pollRequestRef &&
      paymentHotelID &&
      orderID &&
      ((isPreAuthReturn && preAuthRequestRef === orderID) ||
        (isPaymentReturn && paymentRequestRef === orderID))
  );

  // Xendit/payment redirect drops location.state; keep the online-payment step map
  // so goToStep('payment'|'preAuth'|'confirm') still resolves to the right indices.
  const inPaymentReturn = Boolean(normalizedStatus);
  const includePreAuth = preAuthElected || isPreAuthReturn;
  const contentSteps = buildContentSteps(
    dateControl || (isOnlinePayment && inPaymentReturn),
    isEnableSelfie,
    isOnlinePayment,
    includePreAuth
  );
  // Pre-auth is intermediate after payment — not its own stepper label.
  const visibleSteps = contentSteps.filter((step): step is Exclude<ContentStep, 'preAuth'> => step !== 'preAuth');
  const stepLabels = ['Pre CheckIn', ...visibleSteps.map(step => STEP_LABELS[step])];
  const currentStepKey = contentSteps[activeStep - 1];
  // keep latest steps in a ref so goToStep stays stable — otherwise the
  // success-return effect re-runs every render and snaps back to 'payment',
  // blocking the advance to 'preAuth' after trip 1 completes.
  const contentStepsRef = useRef(contentSteps);
  contentStepsRef.current = contentSteps;
  const goToStep = useCallback((key: ContentStep) => {
    setActiveStep(contentStepNumber(contentStepsRef.current, key));
  }, []);
  const stepperActiveStep = currentStepKey
    ? contentStepNumber(visibleSteps, STEPPER_LABEL_STEP[currentStepKey])
    : Math.min(activeStep, stepLabels.length - 1);

  const {
    loading: RegistrationDetailsLoadings,
    data: { PreRegRegistrationDetails } = { PreRegRegistrationDetails: null },
    refetch: refetchRegistrationDetails,
  } = usePreRegRegistrationDetailsQuery({
    variables: {
      HotelID: localstate
        ? localstate?.HotelID
        : JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.Booking?.HotelID,
      BookingID: localstate
        ? localstate?.BookingID
        : JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.Booking?.ID,
      RegistrationID: localstate
        ? localstate?.RegistrationID
        : JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.ID,
    },
    fetchPolicy: 'no-cache',
    onCompleted: data => {
      localStorage.setItem('RegistrationDetails', JSON.stringify(data.PreRegRegistrationDetails));
    },
  });

  const {
    data: { IsXendit } = { IsXendit: null },
  } = useIsXenditQuery({
    fetchPolicy: 'no-cache',
    variables: {
      HotelID: localstate
        ? localstate?.HotelID
        : JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.Booking?.HotelID
    },
  })

  const isXenditSuccessReturn = IsXendit === true && normalizedStatus === 'SUCCESS';

  const {
    data: paymentAttemptStatusData,
    loading: paymentAttemptStatusLoading,
    error: paymentAttemptStatusError,
    refetch: refetchPaymentAttemptStatus,
    startPolling: startPaymentStatusPolling,
    stopPolling: stopPaymentStatusPolling,
  } = usePreRegistrationPaymentAttemptStatusQuery({
    variables: {
      HotelID: paymentHotelID,
      PaymentAttemptID: pollAttemptID,
      RequestRef: pollRequestRef,
    },
    skip: !isXenditSuccessReturn || !hasValidPaymentContinuation,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });

  const paymentDisplayStatus = paymentAttemptStatusData?.PreRegistrationPaymentAttemptStatus?.DisplayStatus;

  const [UpdateIsPreCheckin] = useUpdateIsPreCheckinMutation({
    variables: {
      BookingID: localstate
        ? localstate?.BookingID
        : JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.Booking?.ID,
      RegistrationID: localstate
        ? localstate?.RegistrationID
        : JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.ID,
    },
    onCompleted: () => {
      goToStep('confirm');
    },
  });

  const [UpdateRoomAssign] = useUpdateRoomAssignmentMutation({});
  const [AdvancePaymentUpdate, { loading: loadingAdvancePaymentUpdate }] = useAdvancePaymentUpdateMutation({});
  const [
    CheckInPreReg, 
    { loading: CheckInPreRegLoading}
  ] = useCheckInPreRegMutation({
    fetchPolicy: 'no-cache',
    onCompleted: ({ CheckInPreReg }) => {
      if (CheckInPreReg === true) {
        UpdateIsPreCheckin()
      }
    },
    onError: (error: any) => {
      const message =
        error?.graphQLErrors?.[0]?.message ||
        error?.networkError?.message ||
        error?.message ||
        'Check-in failed';
      snackFunc(message, false);
      // Stay in the flow (don't hard-navigate via handleBack) so the snack
      // remains visible; send the guest back to Choose Room to retry.
      goToStep('chooseRoom');
      setAssignRoom(false);
    },
  })

  useEffect(() => {
    if (!status || IsXendit === null || returnHandledRef.current) return;

    if (IsXendit === true) {
      if (normalizedStatus === 'SUCCESS') {
        returnHandledRef.current = true;
        goToStep(isPreAuthReturn ? 'preAuth' : 'payment');
        return;
      }

      if (cancelledUrl) {
        returnHandledRef.current = true;
        if (isPreAuthReturn) {
          localStorage.removeItem('PreAuthAttemptID');
          localStorage.removeItem('PreAuthRequestRef');
          goToStep('preAuth');
          return;
        }
        const registrationDetails = JSON.parse(localStorage.getItem('RegistrationDetails') as any);
        const selectedRoom = JSON.parse(localStorage.getItem('SelectedRoom') as any);
        UpdateRoomAssign({
          variables: {
            BookingID: registrationDetails?.Booking?.ID,
            RegistrationID: registrationDetails?.ID,
            RoomID: '',
            RoomTypeID: selectedRoom?.RoomTypeID || registrationDetails?.RoomTypeID,
            HotelID: registrationDetails?.Booking?.HotelID,
            IsCompleted: false,
          },
        });
        localStorage.removeItem('PaymentAttemptID');
        localStorage.removeItem('PaymentRequestRef');
        localStorage.removeItem('PreAuthElected');
        goToStep('chooseRoom');
        return;
      }

      returnHandledRef.current = true;
      goToStep(isPreAuthReturn ? 'preAuth' : 'payment');
      return;
    }

    returnHandledRef.current = true;
    if (IsXendit === false) {
      const storedAdvPaymentID = localStorage.getItem('AdvPaymentID');
      const advancePaymentID =
        storedAdvPaymentID &&
        storedAdvPaymentID !== 'undefined' &&
        storedAdvPaymentID !== 'null'
          ? storedAdvPaymentID
          : undefined;

      AdvancePaymentUpdate({
        variables: {
          Status: status?.toLowerCase(),
          OrderID: orderID,
          CheckOutID: localStorage.getItem('CheckOutID'),
          ...(advancePaymentID ? { AdvancePaymentID: advancePaymentID } : {}),
          RegistrationID: JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.ID,
          HotelID: JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.Booking?.HotelID,
          ReceiptNo: localStorage.getItem('ReceiptNo'),
          AppName: 'PreCheckIn',
          IsXendit: IsXendit
        },
      }).then(() => {
        if (normalizedStatus !== 'SUCCESS') {
          UpdateRoomAssign({
            variables: {
              BookingID: JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.Booking?.ID,
              RegistrationID: JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.ID,
              RoomID: '',
              RoomTypeID:
                JSON.parse(localStorage.getItem('SelectedRoom') as any)?.RoomTypeID ||
                JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.ID,
            },
          });
          goToStep('chooseRoom');
        } else {
          CheckInPreReg({
            variables: {
              BookingID: JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.Booking?.ID,
              HotelID: JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.Booking?.HotelID,
              RegistrationID: JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.ID
            }
          })
        }
      }).catch((err) => {
        const message =
          err?.graphQLErrors?.[0]?.message ||
          err?.networkError?.message ||
          err?.message ||
          'Payment update failed';
        goToStep('payment');
        snackFunc(message, false);
      });
    }
  }, [
    status,
    normalizedStatus,
    cancelledUrl,
    IsXendit,
    orderID,
    isPreAuthReturn,
    goToStep,
    AdvancePaymentUpdate,
    CheckInPreReg,
    UpdateRoomAssign,
    snackFunc,
    goToStep,
  ]);

  useEffect(() => {
    if (!isXenditSuccessReturn) return;
    // only place the guest on the status screen once — do not re-force
    // 'payment' after Completed advances them to 'preAuth'
    if (!returnStepPlacedRef.current) {
      returnStepPlacedRef.current = true;
      goToStep(isPreAuthReturn ? 'preAuth' : 'payment');
    }
    if (!hasValidPaymentContinuation) return;

    setPaymentPollingTimedOut(false);
    startPaymentStatusPolling(2000);
    paymentPollingTimeoutRef.current = window.setTimeout(() => {
      stopPaymentStatusPolling();
      setPaymentPollingTimedOut(true);
    }, 60000);

    return () => {
      if (paymentPollingTimeoutRef.current !== null) {
        window.clearTimeout(paymentPollingTimeoutRef.current);
        paymentPollingTimeoutRef.current = null;
      }
      stopPaymentStatusPolling();
    };
  }, [
    isXenditSuccessReturn,
    isPreAuthReturn,
    hasValidPaymentContinuation,
    goToStep,
    startPaymentStatusPolling,
    stopPaymentStatusPolling,
    goToStep,
  ]);

  useEffect(() => {
    if (!paymentDisplayStatus) return;
    const terminalStatuses = [
      'Completed',
      'Authorized',
      'PaymentReceivedActionRequired',
      'AuthorizationReceivedActionRequired',
      'Failed',
      'Cancelled',
      'Expired',
    ];
    if (terminalStatuses.includes(paymentDisplayStatus)) {
      stopPaymentStatusPolling();
      if (paymentPollingTimeoutRef.current !== null) {
        window.clearTimeout(paymentPollingTimeoutRef.current);
        paymentPollingTimeoutRef.current = null;
      }
    }

    if (paymentDisplayStatus === 'Completed' && !completionHandledRef.current) {
      completionHandledRef.current = true;
      setIsRedirectingAfterSuccess(true);
      refetchRegistrationDetails().finally(() => {
        if (successHoldTimeoutRef.current !== null) {
          window.clearTimeout(successHoldTimeoutRef.current);
        }
        successHoldTimeoutRef.current = window.setTimeout(() => {
          successHoldTimeoutRef.current = null;
          setIsRedirectingAfterSuccess(false);
          if (preAuthElected) {
            // allow a later 'Authorized' poll (trip 2) to complete this effect again
            completionHandledRef.current = false;
          }
          setIsRedirectingAfterSuccess(false);
          goToStep(preAuthElected ? 'preAuth' : 'confirm');
        }, SUCCESS_STATUS_HOLD_MS);
      });
    }

    if (paymentDisplayStatus === 'Authorized' && !completionHandledRef.current) {
      completionHandledRef.current = true;
      setIsRedirectingAfterSuccess(true);
      refetchRegistrationDetails().finally(() => {
        if (successHoldTimeoutRef.current !== null) {
          window.clearTimeout(successHoldTimeoutRef.current);
        }
        successHoldTimeoutRef.current = window.setTimeout(() => {
          successHoldTimeoutRef.current = null;
          setIsRedirectingAfterSuccess(false);
          goToStep('confirm');
        }, SUCCESS_STATUS_HOLD_MS);
      });
    }
  }, [paymentDisplayStatus, preAuthElected, refetchRegistrationDetails, stopPaymentStatusPolling, goToStep]);

  useEffect(() => {
    return () => {
      if (successHoldTimeoutRef.current !== null) {
        window.clearTimeout(successHoldTimeoutRef.current);
        successHoldTimeoutRef.current = null;
      }
    };
  }, []);

  const handleBack = () => {
    if (currentStepKey === 'chooseRoom' && assignRoom === true) {
      UpdateRoomAssign({
        variables: {
          BookingID: JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.Booking?.ID,
          RegistrationID: JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.ID,
          RoomID: '',
          RoomTypeID: JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.RoomTypeID,
          IsCompleted: false,
        },
      });
      localStorage.removeItem('SelectedRoom');
      setAssignRoom(false);
    } else if (currentStepKey === 'chooseRoom' && cancelledUrl) {
      setActiveStep(activeStep - 1);
      navigate('/pre-checkin/form/room');
    } else if (activeStep !== 1) {
      setActiveStep(activeStep - 1);
    } else {
      const hotelID = localstate?.HotelID ?? JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.Booking?.HotelID;
      const bookingID = localstate?.BookingID ?? JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.Booking?.ID;
      window.location.href = buildPreCheckInUrl(hotelID, bookingID);
    }
  };

  const renderPaymentStatus = () => (
    <XenditPaymentStatus
      displayStatus={paymentDisplayStatus || undefined}
      loading={paymentAttemptStatusLoading}
      timedOut={paymentPollingTimedOut}
      hasError={Boolean(paymentAttemptStatusError)}
      hasValidContinuation={hasValidPaymentContinuation}
      redirecting={isRedirectingAfterSuccess}
      onRefresh={() => {
        setPaymentPollingTimedOut(false);
        refetchPaymentAttemptStatus();
      }}
    />
  );

  const stepper = (
    <Box display="flex">
      <Stack sx={{ width: '100%' }}>
        <Stepper alternativeLabel activeStep={stepperActiveStep} connector={<StepIconConnector />}>
          {stepLabels.map(label => (
            <Step key={label}>
              <StepLabel StepIconComponent={StepIcon}>
                <Typography fontSize="12px" style={{ marginTop: '-10px', lineHeight: '1.2' }}>
                  {label}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Stack>
    </Box>
  );

  const stepperContent = () => {
    switch (currentStepKey) {
      case 'selfie':
        return (
          <Selfie
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            HotelID={localstate?.HotelID ?? JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.Booking?.HotelID}
            RegistrationID={localstate?.RegistrationID || JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.ID}
            BookingID={localstate?.BookingID ?? JSON.parse(localStorage.getItem('RegistrationDetails') as any)?.Booking?.ID}
          />
        );
      case 'chooseRoom':
        if (isSelfCheckin === true && assignRoom === false) {
          return (
            <ChooseRoom
              activeStep={activeStep}
              setActiveStep={setActiveStep}
              setAssignRoom={setAssignRoom}
              setRoomSelect={setRoomSelect}
              RegistrationDetails={PreRegRegistrationDetails}
              isSelfChooseRoom={isSelfChooseRoom}
              UpdateRoomAssign={UpdateRoomAssign}
            />
          );
        }
        if (isSelfCheckin === true && assignRoom === true) {
          return (
            <AssignRoom
              activeStep={activeStep}
              setActiveStep={setActiveStep}
              roomSelect={roomSelect}
              setRoomSelect={setRoomSelect}
              RegistrationDetails={PreRegRegistrationDetails}
              UpdateRoomAssign={UpdateRoomAssign}
            />
          );
        }
        return <ConfirmCheckIn RegistrationDetails={PreRegRegistrationDetails} />;
      case 'payment':
        if (isXenditSuccessReturn && (isPaymentReturn || !isPreAuthReturn)) {
          return renderPaymentStatus();
        }
        return (
          <Payment
            activeStep={activeStep}
            setActiveStep={setActiveStep}
            RegistrationDetails={PreRegRegistrationDetails}
          />
        );
      case 'preAuth':
        if (isXenditSuccessReturn && isPreAuthReturn) {
          return renderPaymentStatus();
        }
        return <PreAuthorisation RegistrationDetails={PreRegRegistrationDetails} />;
      case 'confirm':
        // After payment redirect location.state is gone; do not gate on dateControl
        // or guests land on ConfirmCheckIn instead of ConfirmPayment.
        if (isOnlinePayment) {
          return <ConfirmPayment RegistrationDetails={PreRegRegistrationDetails} />;
        }
        return <ConfirmCheckIn RegistrationDetails={PreRegRegistrationDetails} />;
      default:
        return <ConfirmCheckIn RegistrationDetails={PreRegRegistrationDetails} />;
    }
  };

  const isLoading =
    RegistrationDetailsLoadings ||
    (IsXendit === false && (loadingAdvancePaymentUpdate || CheckInPreRegLoading));

  return (
    <>
      {isLoading ? (
        <Loading />
      ) : (
        <MainWrapper>
          <Box
            sx={{ p: 2, pb: '96px', background: 'transparent', width: '100%', maxWidth: '800px' }}
            margin="auto"
            bgcolor="#F7FAF7"
            display="flex"
            flexDirection="column"
            minHeight="90vh"
          >
            {(isOnlinePayment || isEnableSelfie) &&
              currentStepKey !== 'payment' &&
              currentStepKey !== 'preAuth' && (
              <Box display="flex" marginBottom="15px" alignItems="center">
                <IconButton
                  style={{ padding: '0px', color: 'black' }}
                  onClick={handleBack}
                >
                  <KeyboardArrowLeftIcon />
                </IconButton>
                <Typography fontSize="15px" fontWeight={600} paddingLeft="5px">
                  {PreRegRegistrationDetails?.Guest?.Contact?.FullName}
                </Typography>
              </Box>
            )}
            <Card
              sx={{
                padding: '0px',
                margin: '0 0 14px 0',
                borderRadius: '5px',
                boxShadow:
                  '0 3px 1px -2px transparent, 2px 4px 4px -2px rgb(0 0 0 / 15%), -2px -1px 4px -2px rgb(0 0 0 / 15%)',
              }}
            >
              <CardContent sx={{ ':last-child': { p: '10px' } }} style={{ padding: '7px' }}>
                {stepper}
              </CardContent>
            </Card>
            {stepperContent()}
          </Box>
        </MainWrapper>
      )}

      {/* keep mounted during loading so snack messages are not unmounted */}
      <SnackBarMsg open={openSnackBar} message={snackBarMessage} onClose={close} />
    </>
  );
};
